import Phaser from 'phaser';
import { GameScene, type CastShadowLayers } from '../GameScene';
import { getBodyFrameDimensions } from '../../sprites/BodySpriteManager';

/**
 * Sombras proyectadas (cast shadow multi-capa según hora del sol) + dropshadow de entidades,
 * extraídas de GameScene (god-object). Cosmético. Lee la orientación del sol (shadowLength/Angle/
 * Alpha) y helpers de la escena vía `this.scene.*`. Ver GameScene §CAST SHADOW / DROPSHADOW.
 */
export class ShadowSystem {
  constructor(private scene: GameScene) {}

  createCastShadowLayers(): CastShadowLayers {
    return { body: null, head: null, weapon: null, shield: null, helm: null, boots: null };
  }

  /**
   * Sincroniza el gemelo negro de UNA capa con su sprite real y lo proyecta en
   * el piso con la afín (rotación `rot` + escala `sx`,`sy`) ya calculada para
   * este frame, pivoteando en los pies (footX, footY). Crea el gemelo lazy.
   */
  private syncCastShadowLayer(
    cast: CastShadowLayers,
    key: keyof CastShadowLayers,
    real: Phaser.GameObjects.Sprite | null,
    footX: number,
    footY: number,
    rot: number,
    cos: number,
    sin: number,
    sx: number,
    sy: number,
    alpha: number
  ): void {
    let twin = cast[key];

    // Capa ausente/oculta este frame: esconder el gemelo si existía.
    if (!real || !real.visible) {
      if (twin) twin.setVisible(false);
      return;
    }

    if (!twin) {
      twin = this.scene.add.sprite(footX, footY, real.texture.key);
      twin.setScrollFactor(1);
      cast[key] = twin;
    }

    twin.setVisible(true);
    twin.setTexture(real.texture.key, real.frame.name);
    twin.setTintFill(0x000000); // silueta negra sólida (reaplicar tras setTexture)
    twin.setOrigin(real.originX, real.originY);
    twin.setFlipX(real.flipX);
    twin.setFlipY(real.flipY);
    twin.setAlpha(alpha);
    twin.setDepth(GameScene.CAST_SHADOW_DEPTH);
    twin.setRotation(rot);
    twin.setScale(sx, sy);

    // Posición = afín aplicada al offset de la capa respecto del pivote de pies.
    const dx = (real.x - footX) * sx;
    const dy = (real.y - footY) * sy;
    twin.setPosition(footX + dx * cos - dy * sin, footY + dx * sin + dy * cos);
  }

  /**
   * Proyecta la silueta completa (todas las capas presentes) en el piso para una
   * entidad. Calcula la afín una vez (rotación según dirección del sol, achatado
   * según largo de sombra) y la aplica a cada capa via syncCastShadowLayer.
   */
  updateCastShadow(
    cast: CastShadowLayers,
    layers: {
      body: Phaser.GameObjects.Sprite;
      head: Phaser.GameObjects.Sprite | null;
      weapon: Phaser.GameObjects.Sprite | null;
      shield: Phaser.GameObjects.Sprite | null;
      helm: Phaser.GameObjects.Sprite | null;
      boots: Phaser.GameObjects.Sprite | null;
    },
    footX: number,
    footY: number,
    alphaMul: number = 1
  ): void {
    if (!GameScene.CAST_SHADOW_ENABLED) return;

    // lean ∈ [-1,1]: dirección (signo) + cuán horizontal (magnitud) según el sol.
    // 6am→cos(PI)=-1 (oeste/izq), mediodía→cos(-PI/2)=0 (recta/corta), 6pm→cos(0)=+1 (este/der).
    const lean = Math.cos(this.scene.shadowAngle) * this.scene.shadowLength;
    const rot = lean * GameScene.CAST_SHADOW_MAX_LEAN;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const sx = 1;
    const sy =
      GameScene.CAST_SHADOW_MIN_SQUASH +
      this.scene.shadowLength * (GameScene.CAST_SHADOW_MAX_STRETCH - GameScene.CAST_SHADOW_MIN_SQUASH);
    const alpha = this.scene.shadowAlpha * alphaMul;

    // Orden: capas bajas primero (boots/body) y altas después (head/helm), todas al
    // mismo depth → el display-list order las apila como la figura real.
    this.syncCastShadowLayer(cast, 'boots', layers.boots, footX, footY, rot, cos, sin, sx, sy, alpha);
    this.syncCastShadowLayer(cast, 'body', layers.body, footX, footY, rot, cos, sin, sx, sy, alpha);
    this.syncCastShadowLayer(cast, 'shield', layers.shield, footX, footY, rot, cos, sin, sx, sy, alpha);
    this.syncCastShadowLayer(cast, 'weapon', layers.weapon, footX, footY, rot, cos, sin, sx, sy, alpha);
    this.syncCastShadowLayer(cast, 'head', layers.head, footX, footY, rot, cos, sin, sx, sy, alpha);
    this.syncCastShadowLayer(cast, 'helm', layers.helm, footX, footY, rot, cos, sin, sx, sy, alpha);
  }

  /** Destruye todos los gemelos de una cast-shadow (teardown de entidad). */
  destroyCastShadow(cast: CastShadowLayers | null): void {
    if (!cast) return;
    cast.body?.destroy();
    cast.head?.destroy();
    cast.weapon?.destroy();
    cast.shield?.destroy();
    cast.helm?.destroy();
    cast.boots?.destroy();
  }

  /**
   * Creates a dropshadow sprite for an entity.
   * The dropshadow width scales to 75% of the body width.
   * This is independent from the shadow system.
   */
  createDropshadowSprite(
    x: number,
    y: number,
    depth: number,
    bodyId: number
  ): Phaser.GameObjects.Sprite {
    const { width: bodyWidth } = getBodyFrameDimensions(bodyId);
    // Escalar para que el ancho del dropshadow sea 75% del ancho del body (25% más pequeño)
    const scaleX = (bodyWidth / GameScene.DROPSHADOW_TEXTURE_WIDTH) * 0.75;
    // Mantener proporción para el alto
    const scaleY = scaleX;

    const dropshadow = this.scene.add.sprite(
      x,
      y + GameScene.DROPSHADOW_OFFSET_Y,
      GameScene.DROPSHADOW_TEXTURE_KEY
    );
    dropshadow.setOrigin(0.5, 0.5);
    dropshadow.setScale(scaleX, scaleY);
    dropshadow.setAlpha(GameScene.DROPSHADOW_ALPHA);
    dropshadow.setDepth(depth);
    dropshadow.setScrollFactor(1);
    return dropshadow;
  }

  /**
   * Updates the dropshadow scale when the entity body changes.
   */
  updateDropshadowScale(
    dropshadow: Phaser.GameObjects.Sprite,
    bodyId: number
  ): void {
    const { width: bodyWidth } = getBodyFrameDimensions(bodyId);
    // Escalar para que el ancho del dropshadow sea 75% del ancho del body (25% más pequeño)
    const scaleX = (bodyWidth / GameScene.DROPSHADOW_TEXTURE_WIDTH) * 0.75;
    const scaleY = scaleX;
    dropshadow.setScale(scaleX, scaleY);
  }
}
